import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSpinner, faUser, faLock
} from '@fortawesome/free-solid-svg-icons';
import wedlLogo from '../../assets/images/wedl_logo.svg';
import wedlHintergrund from '../../assets/images/wedl_hintergrund.svg';
import ForgotPasswordModal from '../../components/auth/ForgotPasswordModal';

const GeneralPortalLogin: React.FC = () => {
  const navigate = useNavigate();
  const { masterToken, supplierId } = useParams<{ masterToken?: string; supplierId?: string }>();
  const location = useLocation();
  
  // Determine workflow type based on URL pattern
  const isArticleWorkflow = location.pathname.includes('/article/');
  const workflowId = isArticleWorkflow ? supplierId : masterToken;
  
  // **GENERAL PORTAL MODE: When no masterToken is provided, this is a general login**
  const isGeneralPortalMode = !workflowId && location.pathname === '/supplier/portal';
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [supplierInfo, setSupplierInfo] = useState<any>(null);
  const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);

  // SUPPLIER PORTAL ISOLATION: Admin-Authentifizierung wird komplett ignoriert
  // Auch wenn ein Admin-Token vorhanden ist, muss sich der Benutzer separat
  // als Lieferant anmelden
  useEffect(() => {
    console.log('🏠 Supplier Portal Login - Admin authentication is ignored');
    console.log('🔐 User must authenticate separately as supplier');
    console.log('🎯 General Portal Mode:', isGeneralPortalMode);
    
    // Ensure we start with a clean supplier session state
    // (but don't clear admin tokens - they should be independent)
    if (!workflowId || isGeneralPortalMode) {
      // Only clear supplier sessions for general portal access
      sessionStorage.removeItem('supplierPortalSession');
      sessionStorage.removeItem('supplierPortalSessionTime');
      sessionStorage.removeItem('supplierInfo');
      console.log('🧹 Cleared previous supplier sessions for fresh login');
    }
  }, [workflowId, isGeneralPortalMode]);

  // Load supplier info from masterToken or supplierId if available
  useEffect(() => {
    let isMounted = true;
    let timeoutId: NodeJS.Timeout;
    
    const loadSupplierInfo = async () => {
      // **PREVENT MULTIPLE SIMULTANEOUS CALLS**
      if (supplierInfo || isLoading) {
        console.log('⏭️ Skipping loadSupplierInfo - already loading or loaded');
        return;
      }
      
      // **ADD DELAY TO PREVENT RAPID SUCCESSIVE CALLS**
      await new Promise(resolve => {
        timeoutId = setTimeout(resolve, 500); // Increased from 100ms to 500ms
      });
      
      if (!isMounted) {
        console.log('⏭️ Component unmounted during delay, aborting loadSupplierInfo');
        return;
      }
      
      try {
        let response;
        
        if (workflowId) {
          if (isArticleWorkflow) {
            // Article workflow - load via supplierId
            console.log(`🔍 Loading supplier info for article workflow, supplierId: ${workflowId}`);
            response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/public/portal/supplier/${workflowId}/info`);
          } else {
            // Questionnaire workflow - load via masterToken
            console.log(`🔍 Loading supplier info for questionnaire workflow, masterToken: ${workflowId}`);
            response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/public/portal/${workflowId}/info`);
          }
          
          if (isMounted && response && response.ok) {
            const data = await response.json();
            if (data.supplier) {
              setSupplierInfo(data.supplier);
              setEmail(data.supplier.email);
              console.log(`✅ Auto-filled email: ${data.supplier.email} for ${isArticleWorkflow ? 'article' : 'questionnaire'} workflow`);
            }
          } else if (response && !response.ok) {
            const errorData = await response.json();
            if (errorData.retryAfter) {
              console.warn(`⏰ Rate limited, retry after ${errorData.retryAfter} seconds`);
              // Don't retry automatically to prevent loops
            } else {
              console.warn('⚠️ Failed to load supplier info:', response.status, errorData.message);
            }
          }
        }
      } catch (error) {
        if (isMounted) {
          console.log('⚠️ Could not load supplier info:', error);
        }
      }
    };

    // Only load if we have a workflowId and haven't loaded info yet
    if (workflowId && !supplierInfo && !isLoading && !isGeneralPortalMode) {
      console.log(`🚀 Starting loadSupplierInfo for workflowId: ${workflowId}, isArticleWorkflow: ${isArticleWorkflow}`);
      loadSupplierInfo();
    }
    
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [workflowId, isArticleWorkflow, supplierInfo, isLoading, isGeneralPortalMode]); // Added dependencies

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Basic validation
    if (!email.trim() || !password.trim()) {
      setError('Bitte geben Sie E-Mail-Adresse und Passwort ein');
      setIsLoading(false);
      return;
    }

    try {
      let response: Response;
      let data: any = null;

      if (isGeneralPortalMode) {
        // **GENERAL PORTAL MODE: Supplier login without specific masterToken**
        console.log('🌐 General portal login attempt');
        
        response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/public/supplier-portal-login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Anmeldung fehlgeschlagen');
        }

        data = await response.json();
        
        // **SAVE GENERAL SESSION**
        sessionStorage.setItem('supplierPortalSession', data.sessionToken);
        sessionStorage.setItem('supplierPortalSessionTime', Date.now().toString());
        sessionStorage.setItem('supplierInfo', JSON.stringify(data.supplier));
        
        // **DISPATCH SESSION UPDATE EVENT**
        window.dispatchEvent(new CustomEvent('supplierSessionUpdate'));
        console.log('🔄 Dispatched supplierSessionUpdate event after general login');
        
        // **REDIRECT TO SUPPLIER'S PORTAL**
        if (data.masterToken) {
          console.log('🏠 Redirecting to supplier portal:', data.masterToken);
          navigate(`/supplier/portal/${data.masterToken}`, { replace: true });
          return;
        } else {
          throw new Error('Kein Portal für diesen Lieferanten gefunden');
        }
        
      } else if (workflowId && !isArticleWorkflow) {
        // Questionnaire workflow - Portal-specific login with master token
        response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/public/portal/${workflowId}/auth`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ username: email, password }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Anmeldung fehlgeschlagen');
        }

        data = await response.json();
        
        // Save session
        sessionStorage.setItem('supplierPortalSession', data.sessionToken);
        sessionStorage.setItem('supplierPortalSessionTime', Date.now().toString());
        sessionStorage.setItem('supplierInfo', JSON.stringify(data.supplier));
        
        // **DISPATCH SESSION UPDATE EVENT**
        window.dispatchEvent(new CustomEvent('supplierSessionUpdate'));
        console.log('🔄 Dispatched supplierSessionUpdate event after questionnaire login');
        
        // **CLEAR ANY EXISTING SUPPLIER CONTEXT - FRESH LOGIN SHOULD START WITH PRIMARY ACCOUNT**
        sessionStorage.removeItem('activeSupplierContext');
        console.log('🧹 Cleared activeSupplierContext - fresh login will use primary account');
        
        // **CLEAR LOOP PROTECTION ON SUCCESSFUL LOGIN**
        sessionStorage.removeItem('lastLoginRedirect');
        sessionStorage.removeItem('lastLoginRedirectTime');
        sessionStorage.removeItem('lastDashboardLoad');
        
        // Check for password change requirement
        if (data.mustChangePassword || data.isFirstLogin) {
          sessionStorage.setItem('passwordChangeRedirect', `/supplier/portal/${workflowId}`);
          sessionStorage.setItem('requirePasswordChange', 'true');
          sessionStorage.setItem('isFirstLogin', data.isFirstLogin ? 'true' : 'false');
          
          console.log('🔄 Redirecting to password change for questionnaire workflow');
          // Use navigate instead of window.location.href to prevent loading issues
          navigate(`/supplier/portal/${workflowId}/change-password`, { replace: true });
          return;
        }
        
        // Short delay to ensure session is saved before redirect
        console.log('🔄 Redirecting to questionnaire workflow dashboard:', workflowId);
        setTimeout(() => {
          navigate(`/supplier/portal/${workflowId}`, { replace: true });
        }, 200); // Increased delay to 200ms

      } else if (workflowId && isArticleWorkflow) {
        // Article workflow - General login with supplier ID
        response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/public/portal/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password, supplierId: workflowId }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Anmeldung fehlgeschlagen');
        }

        data = await response.json();
        
        // Save session
        sessionStorage.setItem('supplierPortalSession', data.sessionToken);
        sessionStorage.setItem('supplierPortalSessionTime', Date.now().toString());
        sessionStorage.setItem('supplierInfo', JSON.stringify(data.supplier));
        
        // **DISPATCH SESSION UPDATE EVENT**
        window.dispatchEvent(new CustomEvent('supplierSessionUpdate'));
        console.log('🔄 Dispatched supplierSessionUpdate event after article workflow login');
        
        // Check for password change requirement
        if (data.mustChangePassword || data.isFirstLogin) {
          sessionStorage.setItem('passwordChangeRedirect', `/supplier/portal/article/${workflowId}/login`);
          sessionStorage.setItem('requirePasswordChange', 'true');
          sessionStorage.setItem('isFirstLogin', data.isFirstLogin ? 'true' : 'false');
          
          console.log('🔄 Redirecting to password change for article workflow');
          // Use navigate instead of window.location.href
          navigate(`/supplier/portal/article/${workflowId}/change-password`, { replace: true });
          return;
        }
        
        // Redirect based on supplier's portal type
        if (data.redirectTo) {
          console.log('🔄 Redirecting via server redirectTo:', data.redirectTo);
          // Short delay to ensure session is saved before redirect
          setTimeout(() => {
            navigate(data.redirectTo, { replace: true });
          }, 100);
        } else {
          // Find appropriate portal for this supplier
          const redirectUrl = `/supplier/portal/${data.masterToken || workflowId}`;
          console.log('🔄 Redirecting to determined portal:', redirectUrl);
          // Short delay to ensure session is saved before redirect
          setTimeout(() => {
            navigate(redirectUrl, { replace: true });
          }, 100);
        }

      } else {
        // General login without master token or supplier ID
        response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/public/portal/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Anmeldung fehlgeschlagen');
        }

        data = await response.json();
        
        // Save session
        sessionStorage.setItem('supplierPortalSession', data.sessionToken);
        sessionStorage.setItem('supplierPortalSessionTime', Date.now().toString());
        sessionStorage.setItem('supplierInfo', JSON.stringify(data.supplier));
        
        // **DISPATCH SESSION UPDATE EVENT**
        window.dispatchEvent(new CustomEvent('supplierSessionUpdate'));
        console.log('🔄 Dispatched supplierSessionUpdate event after general login');
        
        // Check for password change requirement
        if (data.mustChangePassword || data.isFirstLogin) {
          sessionStorage.setItem('passwordChangeRedirect', data.redirectTo || `/supplier/portal/${data.masterToken}`);
          sessionStorage.setItem('requirePasswordChange', 'true');
          sessionStorage.setItem('isFirstLogin', data.isFirstLogin ? 'true' : 'false');
          
          console.log('🔄 Redirecting to password change for general login');
          // Use navigate instead of window.location.href
          navigate(`/supplier/portal/${data.masterToken}/change-password`, { replace: true });
          return;
        }
        
        // Redirect to portal - use navigate for proper React routing
        const redirectUrl = data.redirectTo || `/supplier/portal/${data.masterToken}`;
        console.log('🔄 Redirecting to supplier portal:', redirectUrl);
        // Short delay to ensure session is saved before redirect
        setTimeout(() => {
          navigate(redirectUrl, { replace: true });
        }, 100);
      }

    } catch (error: any) {
      // **SPECIAL HANDLING: Redirect to primary account**
      if (error.message && error.message.startsWith('REDIRECT_TO_PRIMARY:')) {
        const primaryMasterToken = error.message.split(':')[1];
        console.log('🔄 Redirecting to primary account portal:', primaryMasterToken);
        window.location.href = `/supplier/portal/${primaryMasterToken}/login`;
        return;
      }
      
      setError(error.message || 'Anmeldung fehlgeschlagen');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordResetComplete = () => {
    setShowPasswordResetModal(false);
  };

  const handlePasswordReset = async (email: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/public/portal/password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Senden der Reset-E-Mail');
      }

      // Success is handled by the modal itself
    } catch (error: any) {
      throw error; // Re-throw so the modal can handle the error
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center p-8 bg-gray-100"
      style={{
        background: `linear-gradient(rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.1)), url(${wedlHintergrund})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl p-8 border border-gray-200">
          {/* Header with Logo */}
          <div className="text-center mb-8">
            <div className="mb-6">
              <img src={wedlLogo} alt="WEDL Logo" className="w-16 h-16 mx-auto" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Lieferantenportal</h2>
            <p className="text-gray-600">
              {masterToken 
                ? "Melden Sie sich mit Ihren Zugangsdaten an" 
                : "Geben Sie Ihre E-Mail-Adresse und Ihr Passwort ein"}
            </p>
            {supplierInfo && (
              <p className="text-sm text-blue-600 mt-2 font-medium">
                Anmeldung für: {supplierInfo.name}
              </p>
            )}
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FontAwesomeIcon icon={faUser} className="mr-2" />
                E-Mail-Adresse
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                placeholder="ihre.email@beispiel.com"
                required
                disabled={!!supplierInfo?.email}
                autoComplete="email"
                name="email"
                id="email"
              />
              {supplierInfo?.email && (
                <p className="text-xs text-gray-500 mt-1">
                  ✓ E-Mail-Adresse automatisch erkannt
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FontAwesomeIcon icon={faLock} className="mr-2" />
                Passwort
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent transition-all duration-200"
                placeholder="••••••••"
                required
                autoComplete="current-password"
                name="password"
                id="password"
              />
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm font-medium">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-red-600 to-red-700 text-white py-3 px-4 rounded-lg hover:from-red-700 hover:to-red-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center font-medium shadow-lg transition-all duration-200"
            >
              {isLoading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />
                  Anmeldung läuft...
                </>
              ) : (
                'Anmelden'
              )}
            </button>

            {/* Password Reset Option */}
            {email && email.includes('@') && email.length > 5 && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowPasswordResetModal(true)}
                  className="text-sm text-red-600 hover:text-red-800 transition-colors font-medium"
                >
                  Passwort vergessen?
                </button>
              </div>
            )}

            {/* Support Contact */}
            {(!email || !email.includes('@') || email.length <= 5) && (
              <div className="text-center">
                <p className="text-sm text-gray-600">
                  Haben Sie Probleme beim Anmelden?{' '}
                  <a href="mailto:edvhotline@wedl.com" className="text-red-600 hover:text-red-800 font-medium">
                    Kontaktieren Sie uns
                  </a>
                </p>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showPasswordResetModal}
        onClose={handlePasswordResetComplete}
        onSubmit={handlePasswordReset}
      />
    </div>
  );
};

export default GeneralPortalLogin;